import { useState } from "react";
import { Link } from "react-router-dom";
import type { PackageTier, ShowWithPackages } from "../types";
import { PackageSelector } from "./PackageSelector";
import { TripBreakdown } from "./TripBreakdown";
import { BookingButtons } from "./BookingButtons";
import {
  formatCents,
  formatDateLong,
  formatDuration,
  stopsLabel,
} from "../utils/format";

interface Props {
  show: ShowWithPackages;
  /** True for the single cheapest show across all results. */
  isBestDeal: boolean;
  /** Lowest cheapest-package total across all shows, for savings math. */
  cheapestOverallCents: number;
}

function Fact({ icon, children }: { icon: string; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
      <span aria-hidden>{icon}</span>
      {children}
    </span>
  );
}

export function ShowCard({ show, isBestDeal, cheapestOverallCents }: Props) {
  const [tier, setTier] = useState<PackageTier>("cheapest");
  const [expanded, setExpanded] = useState(false);

  const cheapest = show.packages.cheapest;
  const selected = show.packages[tier];
  const deltaFromCheapest = cheapest.total_cents - cheapestOverallCents;

  return (
    <div
      className={
        "overflow-hidden rounded-xl border bg-white shadow-sm transition " +
        (isBestDeal ? "border-brand-300 ring-1 ring-brand-200" : "border-slate-200")
      }
    >
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((e) => !e)}
        className="w-full px-4 py-4 text-left hover:bg-slate-50"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {isBestDeal && (
                <span className="rounded-full bg-brand-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                  Best deal
                </span>
              )}
              <span className="truncate text-base font-semibold text-slate-900">
                {show.venue.city}, {show.venue.state}
              </span>
            </div>
            <div className="mt-0.5 text-sm text-slate-500">
              {formatDateLong(show.show_date)} · {show.venue.name}
            </div>
          </div>

          <div className="shrink-0 text-right">
            <div className="text-xs text-slate-400">from</div>
            <div className="text-xl font-bold text-brand-700">
              {formatCents(cheapest.total_cents)}
            </div>
            {deltaFromCheapest > 0 ? (
              <div className="text-xs text-slate-400">
                +{formatCents(deltaFromCheapest)} vs. cheapest
              </div>
            ) : (
              <div className="text-xs font-medium text-emerald-600">
                Lowest total
              </div>
            )}
          </div>
        </div>

        {/* At-a-glance facts — no expand required */}
        <div className="mt-3 flex flex-wrap gap-2">
          {cheapest.flight_option && (
            <>
              <Fact icon="✈️">{stopsLabel(cheapest.flight_option.stops)}</Fact>
              <Fact icon="⏱️">
                {formatDuration(cheapest.flight_option.total_duration_minutes)} round trip
              </Fact>
              <Fact icon="🛫">{cheapest.flight_option.carrier}</Fact>
            </>
          )}
          {cheapest.hotel_option && (
            <Fact icon="🏨">{cheapest.hotel_option.star_rating}★ hotel</Fact>
          )}
          {show.venue.airport_distance_miles != null && (
            <Fact icon="📍">
              {show.venue.airport_distance_miles} mi from airport
            </Fact>
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-slate-200 bg-slate-50 p-4">
          <PackageSelector tier={tier} onChange={setTier} />
          <div className="mt-4">
            <TripBreakdown pkg={selected} />
          </div>

          <dl className="mt-4 space-y-1 border-t border-slate-200 pt-3 text-xs text-slate-500">
            {selected.flight_option && (
              <div className="flex justify-between gap-4">
                <dt>Flight</dt>
                <dd className="text-right text-slate-700">
                  {selected.flight_option.carrier} ·{" "}
                  {stopsLabel(selected.flight_option.stops)} ·{" "}
                  {formatDuration(selected.flight_option.total_duration_minutes)}
                </dd>
              </div>
            )}
            {selected.hotel_option && (
              <div className="flex justify-between gap-4">
                <dt>Hotel</dt>
                <dd className="text-right text-slate-700">
                  {selected.hotel_option.name} ·{" "}
                  {selected.hotel_option.star_rating}★
                </dd>
              </div>
            )}
          </dl>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <BookingButtons showId={show.show_id} ticketUrl={show.ticket_url} />
            <Link
              to={`/shows/${show.show_id}`}
              className="text-sm font-medium text-brand-700 hover:underline"
            >
              View full details →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
